import React, { useState, useRef } from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import useHover from 'common/useHover';
import KEY_CODES from 'common/KeyCodes';
import Badge, { IconBadge, BadgeProps } from 'components/Badge';

import { OutsideHexSvg, InsideHexSvg, getSvgWidth, getSvgHeight } from 'components/Avatar';

import Styled from './styles';

const { ENTER_KEY, SPACE_KEY } = KEY_CODES;
const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);

interface AvatarProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of the Avatar, in text or <img /> or <Icon /> */
  children: React.ReactNode;
  /** The diameter of the Avatar (height and width). If diameter has a value, it will override the size property. */
  diameter?: string;
  /** The size of the avatar. */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** The shape of the avatar. */
  shape?: 'circle' | 'square' | 'hexagon';
  /** Modifies the visual style to correspond to the status color. Applies to non image Avatars. */
  status?: 'critical' | 'major' | 'minor' | 'warning' | 'cleared' | 'success' | 'non-status';
  /** Place a badge on the Avatar. If diameter property is set, then customStatusBadge property must be used to add a badge.
   * Note: a badge is not allowed on an Avatar with size="small".
  */
  badged?: boolean;
  /** Override default Badge component properties. */
  badgeProps?: Partial<BadgeProps> & { status: any };
  /** Place a custom status badge on Avatar in place of the default badge. */
  customStatusBadge?: React.ReactNode;
  /** Display the Avatar in disabled style. */
  disabled?: boolean;
  /** Controls presence of hover and focus effects. */
  hoverEffect?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Any other props than the `Avatar` props will be provided to the wrapper element. */
const Avatar = React.forwardRef<HTMLElement, AvatarProps>((props: AvatarProps, ref) => getContext(({ RTL, theme }) => {
  const {
    theme: themeProp,
    children,
    diameter,
    size,
    shape,
    status,
    badged,
    badgeProps,
    customStatusBadge,
    disabled,
    hoverEffect,
    ...otherProps
  } = props;
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [hoverInsideRef, isHoveredInside] = useHover();
  const [hoverRef, isHovered] = useHover();
  const hexRef = useRef(null);
  const combinedRefs = useCombinedRefs(hexRef, hoverRef);
  const isImg = (children as JSX.Element).type && (children as JSX.Element).type === 'img';
  const mergedTheme = _merge({}, theme.AVATAR, themeProp.AVATAR);
  // if the user is using the 'old' theme, merge it into the non-status Avatar theme
  const hasOldContainer = themeProp && themeProp.AVATAR && themeProp.AVATAR.container !== undefined;
  const hasOldContent = themeProp && themeProp.AVATAR && themeProp.AVATAR.content !== undefined;
  if (hasOldContainer) {
    mergedTheme['non-status'].container = themeProp.AVATAR.container ? themeProp.AVATAR.container : mergedTheme['non-status'].container;
  }
  if (hasOldContent) {
    mergedTheme['non-status'].content = themeProp.AVATAR.content ? themeProp.AVATAR.content : mergedTheme['non-status'].content;
  }
  // Avatar's with img children use non-status theme.
  const resolvedTheme = isImg ? mergedTheme['non-status'] : mergedTheme[status];
  const isDotBadge = badgeProps && (badgeProps.status === undefined || badgeProps.status === 'dot');

  const renderStatusBadge = () => {
    // if using diameter prop must use customStatusBadge to render the Badge
    if (diameter && !customStatusBadge) return null;
    // badge not allowed on small sized Avatar
    if (size === 'small' && !customStatusBadge) return null;
    if (customStatusBadge) return customStatusBadge;
    if (isDotBadge) {
      return (
        <Styled.BadgeWrapper size={size} shape={shape} RTL={RTL}>
          <Badge border {...badgeProps} />
        </Styled.BadgeWrapper>
      );
    }
    return (
      <Styled.BadgeWrapper size={size} shape={shape} RTL={RTL} $iconBadge>
        <IconBadge {...badgeProps} />
      </Styled.BadgeWrapper>
    );
  };

  // since the inside of the hex blocks the outside from getting the event
  const handleHexClick = (e) => {
    const { onClick } = otherProps;
    hexRef && hexRef.current && hexRef.current.focus();
    onClick && onClick(e);
  };

  const handleFocus = (event) => {
    setFocused(true);
    const { onFocus } = otherProps;
    onFocus && onFocus(event);
  };

  const handleBlur = (event) => {
    setFocused(false);
    const { onBlur } = otherProps;
    onBlur && onBlur(event);
  };

  const handleKeyDown = (event) => {
    if (isSelectionKeyPressed(event.key)) {
      setPressed(true);
    }
    const { onKeyDown } = otherProps;
    onKeyDown && onKeyDown(event);
  };

  const handleKeyUp = (event) => {
    if (isSelectionKeyPressed(event.key)) {
      setPressed(false);
    }
    const { onKeyUp } = otherProps;
    onKeyUp && onKeyUp(event);
  };

  const handleMouseDown = (event) => {
    setPressed(true);
    const { onMouseDown } = otherProps;
    onMouseDown && onMouseDown(event);
  };

  const handleMouseUp = (event) => {
    setPressed(false);
    const { onMouseUp } = otherProps;
    onMouseUp && onMouseUp(event);
  };

  const handleMouseLeave = (event) => {
    setPressed(false);
    const { onMouseLeave } = otherProps;
    onMouseLeave && onMouseLeave(event);
  };

  const Hexagon = (
    <Styled.HexWrapper width={`${getSvgWidth(size) + 5}px`} height={`${getSvgHeight(size) + 4}px`}>
      {badged && renderStatusBadge()}
      <Styled.HexInside
        ref={hoverInsideRef}
        theme={resolvedTheme}
        disabled={disabled}
        hoverEffect={hoverEffect}
        hovered={isHovered || isHoveredInside}
        pressed={pressed}
        onClick={handleHexClick}
        isImg={isImg}
        size={size}
        shape={shape}
        width={isImg ? `${getSvgWidth(size) + 1}px` : undefined}
        height={isImg ? `${getSvgHeight(size)}px` : undefined}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {isImg && <InsideHexSvg size={size} location={{ top: 0, left: 0, zIndex: 1 }} />}
        {children}
      </Styled.HexInside>
      <Styled.HexOutside
        ref={combinedRefs}
        tabIndex={disabled || !hoverEffect ? -1 : 0}
        isImg={isImg}
        width={`${getSvgWidth(size) + 5}px`}
        height={`${getSvgHeight(size) + 4}px`}
        theme={resolvedTheme}
        disabled={disabled}
        hoverEffect={hoverEffect}
        hovered={isHovered || isHoveredInside}
        pressed={pressed}
        {...otherProps}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleHexClick}
      >
        <>
          <OutsideHexSvg theme={resolvedTheme} size={size} hoverEffect={hoverEffect} focused={focused} disabled={disabled} />
          {!isImg && <InsideHexSvg size={size} location={{ top: '0.125rem', left: '0.125rem' }} />}
        </>
      </Styled.HexOutside>
    </Styled.HexWrapper>
  );

  const CircleSquare = (
    <Styled.Wrapper
      RTL={RTL}
      ref={ref}
      diameter={diameter}
      size={diameter === undefined ? size : undefined}
      shape={shape}
      theme={resolvedTheme}
      tabIndex={disabled || !hoverEffect ? -1 : 0}
      disabled={disabled}
      hoverEffect={hoverEffect}
      isImg={isImg}
      pressed={pressed}
      {...otherProps}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <Styled.ImageOverlay
        theme={resolvedTheme}
        size={size}
        shape={shape}
        isImg={isImg}
        hoverEffect={hoverEffect}
        pressed={pressed}
        disabled={disabled}
      />
      {children}
      {badged && renderStatusBadge()}
    </Styled.Wrapper>
  );

  return shape === 'hexagon' ? Hexagon : CircleSquare;
}));

Avatar.displayName = 'Avatar';

Avatar.defaultProps = {
  diameter: undefined,
  size: 'medium', // backward compatibility as diameter=2rem (medium) was the default size
  shape: 'circle',
  status: 'non-status',
  badged: false,
  customStatusBadge: undefined,
  disabled: false,
  hoverEffect: false, // default false for backward compatibility
  badgeProps: {
    elevationIndex: 0,
    status: undefined,
    children: undefined, // the icon
  },
  theme: {}
};

export { Avatar as default, AvatarProps };
